import { PrimaryButton } from "@rpforms/shared/build/components/layout/Button";
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import useForm from "react-hook-form";

export interface IZugferdBtFields {
    bt_10_buyer_reference: string;
    bt_13_buyer_order_reference: string;
    bt_19_cost_center: string;
    bt_46_buyer_identifier: string;
}

export interface IZugferdBtPayload {
    buyer_order_reference_number: string;
    zugferd_bt_fields: IZugferdBtFields;
}

interface IModalBuyerOrderReference {
    entry: any;
    onSave: (payload: IZugferdBtPayload) => void;
    onClose: () => void;
    show: boolean;
}

interface IReferenceSources {
    customerName: string;
    customerNumber: string;
    entityName: string;
    sellerTradePartyId: string;
    bt10CustomerDefault: string;
    bt46CustomerDefault: string;
    bt19EntityDefault: string;
}

interface IFieldRenderConfig {
    key: keyof IZugferdBtFields;
    label: string;
    placeholder?: string;
    helpText?: string;
}

const toStringValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return "";
    }
    return String(value);
};

const isPresent = (value: string): boolean => value.trim().length > 0;

const firstPresent = (...values: string[]): string => {
    for (const value of values) {
        if (isPresent(value)) {
            return value;
        }
    }
    return "";
};

const normalizeBtFields = (fields: IZugferdBtFields): IZugferdBtFields => {
    const normalize = (key: keyof IZugferdBtFields, value: string): string => {
        const trimmed = toStringValue(value).trim();
        if (key === "bt_10_buyer_reference" || key === "bt_19_cost_center") {
            return trimmed.replace(/\s+/g, "");
        }
        return trimmed;
    };

    return {
        bt_10_buyer_reference: normalize("bt_10_buyer_reference", fields.bt_10_buyer_reference),
        bt_13_buyer_order_reference: normalize(
            "bt_13_buyer_order_reference",
            fields.bt_13_buyer_order_reference
        ),
        bt_19_cost_center: normalize("bt_19_cost_center", fields.bt_19_cost_center),
        bt_46_buyer_identifier: normalize("bt_46_buyer_identifier", fields.bt_46_buyer_identifier),
    };
};

const defaultFieldSources: Record<keyof IZugferdBtFields, string> = {
    bt_10_buyer_reference: "(Kein Standardwert – bitte manuell eintragen)",
    bt_13_buyer_order_reference: "(Kein Standardwert – bitte manuell eintragen)",
    bt_19_cost_center: "(Kein Standardwert – bitte manuell eintragen)",
    bt_46_buyer_identifier: "(Kein Standardwert – bitte manuell eintragen)",
};

interface IInitialFieldState {
    values: IZugferdBtFields;
    sources: Record<keyof IZugferdBtFields, string>;
}

const buildInitialFieldState = (entry: any): IInitialFieldState => {
    const saved = entry?.zugferd_bt_fields || {};
    const entity = entry?.entity || {};
    const customer = entry?.customer || entity?.customer || {};

    const supplierNumber = toStringValue(customer?.zugferd_supplier_number);
    const customerBt10Default = toStringValue(customer?.zugferd_bt_10_buyer_reference_default);
    const customerBt46Default = toStringValue(customer?.zugferd_bt_46_buyer_identifier_default);
    const entityCostCenterDefault = toStringValue(entity?.zugferd_bt_19_cost_center_default);
    const savedCostCenter = toStringValue(saved?.bt_19_cost_center) || entityCostCenterDefault;

    const values: IZugferdBtFields = {
        bt_10_buyer_reference: firstPresent(
            toStringValue(saved?.bt_10_buyer_reference),
            customerBt10Default
        ),
        bt_13_buyer_order_reference: firstPresent(
            toStringValue(saved?.bt_13_buyer_order_reference),
            toStringValue(entry?.buyer_order_reference_number)
        ),
        bt_19_cost_center: firstPresent(
            toStringValue(saved?.bt_19_cost_center),
            entityCostCenterDefault
        ),
        bt_46_buyer_identifier: firstPresent(
            toStringValue(saved?.bt_46_buyer_identifier),
            customerBt46Default,
            supplierNumber
        ),
    };

    const sources: Record<keyof IZugferdBtFields, string> = {
        ...defaultFieldSources,
    };

    sources.bt_13_buyer_order_reference = isPresent(
        toStringValue(saved?.bt_13_buyer_order_reference)
    )
        ? "(Formulardaten: gespeicherte ZUGFeRD-Daten dieser Rechnung)"
        : isPresent(toStringValue(entry?.buyer_order_reference_number))
        ? "(Bestehende Bestellnummer aus dem Formular)"
        : "(Kein Standardwert – bitte manuell eintragen)";

    sources.bt_19_cost_center = isPresent(toStringValue(saved?.bt_19_cost_center))
        ? "(Formulardaten: gespeicherte ZUGFeRD-Daten dieser Rechnung)"
        : isPresent(entityCostCenterDefault)
        ? "(Objekt: Standard Kostenstelle BT-19 des Mandanten)"
        : "(Kein Standardwert – bitte manuell eintragen)";

    sources.bt_10_buyer_reference = isPresent(toStringValue(saved?.bt_10_buyer_reference))
        ? "(Formulardaten: gespeicherte ZUGFeRD-Daten dieser Rechnung)"
        : isPresent(customerBt10Default)
        ? "(Kunde: Standard Käuferreferenz BT-10)"
        : "(Kein Standardwert – bitte manuell eintragen)";

    sources.bt_46_buyer_identifier = isPresent(toStringValue(saved?.bt_46_buyer_identifier))
        ? "(Formulardaten: gespeicherte ZUGFeRD-Daten dieser Rechnung)"
        : isPresent(customerBt46Default)
        ? "(Kunde: Standard Käuferkennung BT-46)"
        : isPresent(supplierNumber)
        ? "(Kunde: Lieferantennummer / zugferd_supplier_number)"
        : "(Kein Standardwert – bitte manuell eintragen)";

    return { values, sources };
};

const buildReferenceSources = (entry: any): IReferenceSources => {
    const entity = entry?.entity || {};
    const customer = entry?.customer || entity?.customer || {};

    return {
        customerName: toStringValue(customer?.name),
        customerNumber: toStringValue(customer?.customer_nr),
        entityName: toStringValue(entity?.name),
        sellerTradePartyId: toStringValue(customer?.zugferd_supplier_number),
        bt10CustomerDefault: toStringValue(customer?.zugferd_bt_10_buyer_reference_default),
        bt46CustomerDefault: toStringValue(customer?.zugferd_bt_46_buyer_identifier_default),
        bt19EntityDefault: toStringValue(entity?.zugferd_bt_19_cost_center_default),
    };
};

const btFieldConfig: IFieldRenderConfig[] = [
    {
        key: "bt_13_buyer_order_reference",
        label: "BT-13 Bestellnummer (BuyerOrderReferencedDocument)",
        placeholder: "z. B. PO-12345",
    },
    {
        key: "bt_10_buyer_reference",
        label: "BT-10 Käuferreferenz (BuyerReference)",
        placeholder: "z. B. Projekt-123, Bestellref. XY",
        helpText:
            "Referenz, die der Käufer dem Verkäufer mitgibt (z. B. Projektnummer, interne Referenz).",
    },
    {
        key: "bt_19_cost_center",
        label: "BT-19 Kostenstelle (CostCenter)",
        placeholder: "z. B. KST-4711",
    },
    {
        key: "bt_46_buyer_identifier",
        label: "BT-46 Käuferkennung (BuyerIdentifier)",
        placeholder: "z. B. Kunde-4711, Lieferantennummer",
        helpText:
            "Kennung des Käufers im System des Verkäufers (z. B. Kundennummer, Lieferantennummer).",
    },
];

export const ModalBuyerOrderReference = ({
    entry,
    onSave,
    onClose,
    show,
}: IModalBuyerOrderReference) => {
    const [btFields, setBtFields] = useState<IZugferdBtFields>(
        () => buildInitialFieldState(entry).values
    );
    const [initialSources, setInitialSources] = useState<Record<keyof IZugferdBtFields, string>>(
        () => buildInitialFieldState(entry).sources
    );
    const [initialValues, setInitialValues] = useState<IZugferdBtFields>(
        () => buildInitialFieldState(entry).values
    );
    const referenceSources = buildReferenceSources(entry);

    useEffect(() => {
        const state = buildInitialFieldState(entry);
        setBtFields(state.values);
        setInitialValues(state.values);
        setInitialSources(state.sources);
    }, [entry]);

    const { handleSubmit } = useForm();

    const onSubmit = () => {
        const normalized = normalizeBtFields(btFields);
        onSave({
            buyer_order_reference_number: normalized.bt_13_buyer_order_reference || "",
            zugferd_bt_fields: normalized,
        });
    };

    const onInputChange = (key: keyof IZugferdBtFields, value: string) => {
        setBtFields((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const sourceForField = (key: keyof IZugferdBtFields): string => {
        if (toStringValue(btFields[key]) !== toStringValue(initialValues[key])) {
            const orig = initialSources[key].replace(/^\(|\)$/g, "");
            return `(Manuell geändert – ursprünglich: ${orig})`;
        }
        return initialSources[key];
    };

    return (
        <Modal show={show} onHide={onClose} size="lg">
            <form onSubmit={handleSubmit(onSubmit)}>
                <Modal.Header closeButton>
                    <Modal.Title>ZUGFeRD Referenzen bearbeiten</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <h6>Formulardaten (pro Rechnung)</h6>
                    <p className="text-muted" style={{ marginTop: "-4px" }}>
                        Initial mit Standards vorbelegt.
                    </p>

                    {btFieldConfig.map((field) => (
                        <div key={field.key} style={{ marginBottom: "14px" }}>
                            <label htmlFor={field.key}>{field.label}</label>
                            {field.helpText && (
                                <small
                                    className="text-muted d-block"
                                    style={{ marginTop: "-2px", marginBottom: "4px" }}
                                >
                                    {field.helpText}
                                </small>
                            )}
                            <input
                                id={field.key}
                                type="text"
                                className="form-control"
                                placeholder={field.placeholder || ""}
                                value={btFields[field.key] || ""}
                                onChange={(e) => onInputChange(field.key, e.target.value)}
                            />
                            <small className="text-muted d-block" style={{ marginTop: "4px" }}>
                                {sourceForField(field.key)}
                            </small>
                        </div>
                    ))}

                    <hr />
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={onClose}>
                        Abbrechen
                    </button>
                    <PrimaryButton type="submit">Speichern</PrimaryButton>
                </Modal.Footer>
            </form>
        </Modal>
    );
};
